import { motion } from "framer-motion";
import { CLUB_EDITIONS, DESIGN_ARCHIVE, LIFESTYLE } from "@/lib/watchImages";
import WatchImage from "@/components/site/WatchImage";

export const TID_CLUB_SECTION = "section-club-editions";
export const clubEditionTid = (slug) => `club-edition-${slug}`;
export const archiveStudyTid = (slug) => `archive-study-${slug}`;

export default function ClubEditions() {
  return (
    <section
      id="club-editions"
      data-testid={TID_CLUB_SECTION}
      className="relative py-28 sm:py-40 border-t border-[rgba(197,160,89,0.15)] overflow-hidden"
    >
      {/* Ambient backdrop: Black member on the terrace, champagne */}
      <div className="absolute inset-0 pointer-events-none">
        <img
          src={LIFESTYLE.terraceChampagne.src}
          alt=""
          className="w-full h-full object-cover object-[20%_center] opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-[#14201a] via-[#14201a]/65 to-[#14201a]/30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#14201a]/80 via-transparent to-[#14201a]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-3xl"
        >
          <div className="flex items-center gap-3">
            <span className="gold-rule" />
            <span className="overline">The Club Editions</span>
          </div>
          <h2 className="font-serif-display text-[#F5F2EB] font-light text-balance text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mt-6">
            Made for one{" "}
            <span className="italic text-[#C5A059]">club at a time</span>.
          </h2>
          <p className="text-[#A8A9AD] text-base sm:text-lg leading-relaxed mt-8 max-w-xl">
            Club Editions are stainless steel and capped at 100 pieces.
            Each one is built for a single members-only club — I work
            with the committee on the dial, the hands, and the engraving.
            Once a club's hundred are out, that reference is closed for
            good.
          </p>
        </motion.div>

        {/* Hero showcase: Club Editions — flex-wrap centers orphan rows */}
        <div className="mt-16 flex flex-wrap justify-center">
          {CLUB_EDITIONS.map((w, i) => (
            <motion.figure
              key={w.slug}
              data-testid={clubEditionTid(w.slug)}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.9, delay: i * 0.1, ease: "easeOut" }}
              className="group relative bg-[#0C0F0A] p-8 sm:p-12 flex flex-col w-full md:w-1/2 lg:w-1/3 border border-[rgba(197,160,89,0.15)] -mt-px -ml-px box-border"
            >
              <div className="relative aspect-square w-full overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#161B14] via-[#0C0F0A] to-[#0C0F0A]" />
                <WatchImage
                  src={w.front}
                  alt={`CLEMENT COURSE I — ${w.name} Club Edition`}
                  fallbackTitle={w.name}
                  fallbackSub={`${w.ref} · Club Edition`}
                  loading={i === 0 ? "eager" : "lazy"}
                  className="transition-transform duration-700 group-hover:scale-105"
                  testId={`club-${w.slug}-image`}
                />
                <div className="absolute top-4 left-4 overline">
                  Club Edition · {w.pieces}
                </div>
              </div>
              <figcaption className="mt-8">
                <div className="overline mb-3">
                  {w.ref} · {w.model}
                </div>
                <h3 className="font-serif-display text-3xl sm:text-4xl text-[#F5F2EB] tracking-tight font-light leading-tight">
                  {w.name}
                </h3>
                <p className="text-[#A8A9AD] text-sm sm:text-base leading-relaxed mt-4 max-w-md">
                  {w.notes}
                </p>
                <div className="hairline mt-8" />
                <div className="mt-5 flex items-center justify-between text-[10px] uppercase tracking-[0.28em] text-[#C5A059]">
                  <span>{w.metal}</span>
                  <span>100 per club · By commission</span>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>

        {/* Design Archive — prototype studies */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="mt-24 sm:mt-32"
        >
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <div>
              <div className="flex items-center gap-3">
                <span className="gold-rule" />
                <span className="overline">Design Archive</span>
              </div>
              <h3 className="font-serif-display text-[#F5F2EB] font-light text-2xl sm:text-3xl mt-4 max-w-2xl">
                A few bezels I designed but didn't end up making.
              </h3>
            </div>
            <p className="overline text-[#A8A9AD] max-w-xs">
              Earlier directions — kept here for the collectors who like
              the story behind a piece.
            </p>
          </div>

          <div className="mt-12 flex flex-wrap justify-center">
            {DESIGN_ARCHIVE.map((w, i) => (
              <motion.figure
                key={w.slug}
                data-testid={archiveStudyTid(w.slug)}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: "easeOut" }}
                className="relative bg-[#0C0F0A] p-6 group w-full sm:w-1/3 border border-[rgba(197,160,89,0.12)] -mt-px -ml-px box-border"
              >
                <div className="relative aspect-square w-full overflow-hidden grayscale-[0.15] group-hover:grayscale-0 transition-all duration-700">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#161B14] via-[#0C0F0A] to-[#0C0F0A]" />
                  <WatchImage
                    src={w.front}
                    alt={`CLEMENT — ${w.name} archive study`}
                    fallbackTitle={w.name}
                    fallbackSub={`Archive · ${w.ref}`}
                    loading="lazy"
                    className="opacity-90 group-hover:opacity-100 transition-opacity duration-700"
                    testId={`archive-${w.slug}-image`}
                  />
                  <div className="absolute top-3 left-3 text-[9px] uppercase tracking-[0.32em] text-[#A8A9AD]">
                    Archive · {w.ref}
                  </div>
                </div>
                <figcaption className="mt-5">
                  <h4 className="font-serif-display text-xl text-[#F5F2EB] font-light">
                    {w.name}
                  </h4>
                  <p className="text-[#A8A9AD] text-xs leading-relaxed mt-2">
                    {w.notes}
                  </p>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </motion.div>

        <div className="mt-12 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-[10px] uppercase tracking-[0.32em] text-[#A8A9AD]">
          <span>One club at a time · 100 pieces · Stainless steel</span>
          <a
            href="#waitlist"
            className="text-[#C5A059] hover:text-[#F5F2EB] transition-colors"
          >
            Ask about a club commission →
          </a>
        </div>
      </div>
    </section>
  );
}
